'use client';

import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, ButtonBase, Collapse, Divider, Typography } from '@mui/material';

import { SportsFuturesIcon, SportsLiveIcon, SquareIcon } from '@/components';
import { BottomFilterDrawer } from '@/components/common';
import { checkIsCategoryActive, hasSubLeagues, isAutomated, useSportsSidebar, useT } from '@/hooks';
import { SidebarItem, SportsSidebarCategory } from '@/types';
import { SportsFallbackIcon } from '../SportsFallbackIcon';

interface SportsMobileBottomBarProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  sidebarContext: ReturnType<typeof useSportsSidebar>;
}

const MobileCategoryItem = ({
  category,
  activeId,
  onCategoryClick,
}: {
  category: SportsSidebarCategory;
  activeId: string | number | null;
  onCategoryClick: (category: SidebarItem) => void;
}) => {
  const isAutomatedCategory = isAutomated(category);
  const leaguesArray = isAutomatedCategory ? category.leagues : [];
  const hasLeagues = hasSubLeagues(category);

  const isCatActive = checkIsCategoryActive(category, activeId);

  const activeLeague = isAutomatedCategory
    ? leaguesArray.find((item) => String(activeId) === String(item.id))
    : null;

  const [isExpanded, setIsExpanded] = useState(!!activeLeague);

  const name = category.category || (category as any).sport || '';
  const image = category.img || null;
  const count = category.totalEvents || 0;

  const handleClick = () => {
    if (hasLeagues) {
      setIsExpanded(!isExpanded);
    } else {
      onCategoryClick(category);
    }
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1.5,
          cursor: 'pointer',
          bgcolor: isCatActive && !hasLeagues ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}
        >
          <SquareIcon
            src={image}
            alt={name}
            size={20}
            fallback={<SportsFallbackIcon size={20} />}
          />
        </Box>

        <Typography
          sx={{
            fontSize: '0.9375rem',
            fontWeight: isCatActive ? 600 : 500,
            color: isCatActive ? 'primary.main' : 'text.primary',
            flex: 1,
          }}
        >
          {name}
        </Typography>

        {!hasLeagues && count > 0 && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              ml: 1,
            }}
          >
            {count}
          </Typography>
        )}

        {hasLeagues &&
          (isExpanded ? (
            <KeyboardArrowUpIcon sx={{ color: 'text.secondary', fontSize: '1.25rem', ml: 1 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: 'text.secondary', fontSize: '1.25rem', ml: 1 }} />
          ))}
      </Box>

      {hasLeagues && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {leaguesArray.map((league) => {
              const isLeagueActive = String(activeId) === String(league.id);
              return (
                <Box
                  key={league.id}
                  onClick={() => onCategoryClick(league)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: 7,
                    pr: 3,
                    py: 1.25,
                    cursor: 'pointer',
                    bgcolor: isLeagueActive ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {league.image && (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <SquareIcon
                        src={league.image}
                        alt={league.name}
                        size={16}
                        fallback={<SportsFallbackIcon size={16} />}
                      />
                    </Box>
                  )}
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: isLeagueActive ? 600 : 500,
                      color: isLeagueActive ? 'primary.main' : 'text.secondary',
                      flex: 1,
                    }}
                  >
                    {league.name}
                  </Typography>
                  {league.eventCount > 0 && (
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                      }}
                    >
                      ({league.eventCount})
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export const SportsMobileBottomBar = ({
  open,
  onClose,
  onOpen,
  sidebarContext,
}: SportsMobileBottomBarProps) => {
  const { state, handlers } = sidebarContext;
  const { allItems, activeId, sportSlug } = state;
  const { handleCategoryClick } = handlers;
  const { t } = useT();

  const isLiveActive = sportSlug === 'live';
  const isFuturesActive = sportSlug === 'futures';

  const handleLiveClick = () => {
    handleCategoryClick({
      sport: 'live',
      category: t('sports.live', { defaultValue: 'Live' }),
    } as SidebarItem);
    onClose();
  };

  const handleFuturesClick = () => {
    handleCategoryClick({
      sport: 'futures',
      category: t('sports.futures', { defaultValue: 'Futures' }),
    } as SidebarItem);
    onClose();
  };

  const handleItemClick = (category: SportsSidebarCategory) => {
    handleCategoryClick(category);
    onClose();
  };

  const renderList = allItems.map((category) => (
    <MobileCategoryItem
      key={category.category}
      category={category}
      activeId={activeId}
      onCategoryClick={handleItemClick}
    />
  ));

  return (
    <BottomFilterDrawer open={open} onClose={onClose} onOpen={onOpen}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <ButtonBase
            onClick={handleLiveClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              borderRadius: '12px',
              bgcolor: 'action.hover',
              color: isLiveActive ? 'primary.main' : 'text.primary',
              width: '100%',
              gap: 1.5,
            }}
          >
            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center' }}>
              <SportsLiveIcon />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {t('sports.live', { defaultValue: 'Live' })}
            </Typography>
          </ButtonBase>

          <ButtonBase
            onClick={handleFuturesClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              borderRadius: '12px',
              bgcolor: 'transparent',
              color: isFuturesActive ? 'primary.main' : 'text.primary',
              width: '100%',
              gap: 1.5,
            }}
          >
            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center' }}>
              <SportsFuturesIcon />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {t('sports.futures', { defaultValue: 'Futures' })}
            </Typography>
          </ButtonBase>
        </Box>

        <Box sx={{ px: 2 }}>
          <Divider sx={{ borderStyle: 'dashed', borderColor: 'divider' }} />
        </Box>

        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            {t('sports.allSports', { defaultValue: 'All Sports' }).toUpperCase()}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>{renderList}</Box>
      </Box>
    </BottomFilterDrawer>
  );
};
